import { OutlinedInput, Stack, Typography } from "@mui/material";
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLazyValidateCouponQuery } from "src/api/services/couponsApi";
import { Coupon } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useProduct } from "src/context/ProductProvider";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { LocalOffer } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";

interface Props {
  setCoupon: (coupon: Coupon | null) => void;
  coupon: Coupon | null;
}

const ApplyCoupon = ({ setCoupon, coupon }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get("coupon") ?? "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validate] = useLazyValidateCouponQuery();
  const productSlug = useProduct((p) => p.slug);
  const { t } = useTranslation();
  const page = "payment-information";
  const component = "applyCoupon";

  const submitted = useMemo(() => {
    return error.length > 0 || success.length > 0;
  }, [error.length, success.length]);

  const handleCode = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setCode(e.target.value);
      if (error.length > 0) {
        setError("");
      }

      if (success.length > 0) {
        setSuccess("");
      }
    },
    [error.length, success.length]
  );

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (success.length > 0 || error.length > 0) {
        setCoupon(null);
        setSuccess("");
        setError("");
        setCode("");
        searchParams.delete("coupon");
        setSearchParams(searchParams);
        return;
      }

      validate({ couponCode: code, productSlug })
        .unwrap()
        .then((data) => {
          if (data != null && data.is_active) {
            setSuccess("Applied");
            setCoupon(data);
            searchParams.set("coupon", data.name);
            setSearchParams(searchParams);
          } else {
            setError("Not valid code");
          }
        })
        .catch(() => {
          setError("Not valid code");
        });
    },
    [
      code,
      error.length,
      productSlug,
      searchParams,
      setCoupon,
      setSearchParams,
      success.length,
      validate,
    ]
  );

  useEffect(() => {
    if (searchParams.get("coupon") != null) {
      const code = searchParams.get("coupon") || "";

      validate({ couponCode: code, productSlug })
        .unwrap()
        .then((data) => {
          if (data != null && data.is_active) {
            setSuccess("Applied");
            setCode(data.name);
            setCoupon(data);
          } else {
            setError("Not valid code");
          }
        })
        .catch(() => {
          setError("Not valid code");
        });
    }
  }, []);

  return (
    <Stack gap={2}>
      <Stack component="form" onSubmit={handleSubmit} gap={1}>
        <OutlinedInput
          value={code}
          onChange={handleCode}
          placeholder="COUPON"
          id="outlined-adornment"
          type="text"
          sx={{
            boxShadow: `
            0px 3px 1px -2px rgba(0, 0, 0, 0.2),
            0px 2px 2px 0px rgba(0, 0, 0, 0.14),
          `,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : success ? "green" : "#ccc",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : success ? "green" : "#999",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : success ? "green" : "#00B6B9",
            },
            borderRadius: "10px",
            width: "100%",
            color: "#000",
            margin: 0,
            padding: "8px 12px 8px 8px",
          }}
          endAdornment={
            <LoadingButton
              color={error ? "error" : success ? "success" : "primary"}
              type="submit"
              disabled={code.length === 0}
              loading={false}
              sx={{ padding: "6px 30px", alignItems: "center" }}
              variant="contained"
              endIcon={
                submitted ? (
                  <CloseIcon fontSize="inherit" />
                ) : (
                  <SyncIcon fontSize="inherit" />
                )
              }
            >
              {submitted
                ? t(`${page}.${component}.clearButton.text`, "Clear")
                : t(`${page}.${component}.applyButton.text`, "Apply")}
            </LoadingButton>
          }
        />
        <Stack marginLeft={1}>
          {error ? (
            <Typography
              component={Stack}
              direction="row"
              alignItems="center"
              gap={1}
              color="error"
            >
              {" "}
              <BlockIcon />
              {t(`${page}.${component}.error`, "Not valid code")}
            </Typography>
          ) : null}
          {success ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                color="success"
                component={Stack}
                direction="row"
                alignItems="center"
                gap={1}
              >
                <CheckIcon />
                {t(`${page}.${component}.success`, "Applied")}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
      {coupon ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            component={Stack}
            direction="row"
            alignItems="center"
            gap={1}
            fontSize={"18px"}
            color="success"
          >
            Coupon <LocalOffer />
          </Typography>

          <Typography fontSize={"18px"} color="success">
            -${coupon.amount}
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default ApplyCoupon;
